/**
 * Mensagens de validação assertivas e específicas
 * Fornece feedback claro e acionável para o usuário
 */

export const ValidationMessages = {
  // ===== TELA DE LOGIN =====
  LOGIN: {
    EMAIL_REQUIRED: "Por favor, informe o e-mail para continuar",
    EMAIL_INVALID: "E-mail inválido. Verifique o formato (exemplo: seu.email@empresa.com)",
    EMAIL_NOT_FOUND: "E-mail não encontrado em nosso sistema. Verifique se está cadastrado ou entre em contato com o suporte",
    SEND_CODE_ERROR: "Erro ao enviar código. Verifique sua conexão com a internet e tente novamente",
    CODE_REQUIRED: "Digite o código de 6 dígitos enviado para seu e-mail",
    CODE_INVALID: "Código inválido ou expirado. Solicite um novo código",
    CODE_VERIFICATION_ERROR: "Erro ao validar o código. Tente novamente ou solicite um novo código",
  },

  // ===== RELATÓRIOS DE AUDIÊNCIA =====
  HEARING_REPORT: {
    // Conciliação
    CONCILIATION_REQUIRED: "Indique se houve conciliação - este é um campo obrigatório",
    AGREEMENT_AMOUNT_REQUIRED: "Informe o valor total do acordo",
    AGREEMENT_AMOUNT_INVALID: "Valor inválido. Use o formato R$ 0,00",
    INSTALLMENTS_REQUIRED: "Informe a quantidade de parcelas",
    INSTALLMENTS_INVALID: "Quantidade de parcelas deve ser um número maior que zero",
    PAYMENT_METHOD_REQUIRED: "Especifique a forma de pagamento (ex: boleto, transferência, etc)",
    COURT_PROPOSAL_REQUIRED: "Indique se houve proposta do juízo",
    IN_HEARING_MINUTES_REQUIRED: "Confirme se as informações constam em ata",

    // Instrução
    AUTHOR_TESTIMONY_REQUIRED: "Indique se houve depoimento do autor - obrigatório",
    CONFESSION_REQUIRED: "Indique se houve confissão de algum fato",
    CONFESSION_DESCRIPTION_REQUIRED: "Descreva o fato confessado (mínimo 50 caracteres)",
    CONFESSION_DESCRIPTION_TOO_SHORT: "A descrição deve ter pelo menos 50 caracteres",
    COMPANY_TESTIMONY_REQUIRED: "Indique se houve depoimento da empresa/preposto",
    OTHER_DEFENDANT_TESTIMONY_REQUIRED: "Indique se houve depoimento de outro réu/reclamada",
    WITNESS_TESTIMONY_REQUIRED: "Indique se houve depoimento de testemunhas",
    WITNESS_EVALUATION_REQUIRED: "Descreva sua avaliação do depoimento das testemunhas (mínimo 50 caracteres)",
    WITNESS_EVALUATION_TOO_SHORT: "A avaliação deve ter pelo menos 50 caracteres",
    COMPANY_EXCLUSION_REQUIRED: "Indique se foi solicitada a exclusão da empresa do processo",

    // Feedback do Advogado
    LAWYER_NAME_REQUIRED: "Informe o nome do advogado que participou da audiência",
    LAWYER_NAME_INVALID: "Nome do advogado inválido",
    DEFENSE_COPY_REQUIRED: "Indique se você recebeu cópia da defesa",
    PROCESS_GUIDANCE_REQUIRED: "Indique se recebeu orientações sobre o processo",
    TESTIMONY_GUIDANCE_REQUIRED: "Indique se recebeu orientações sobre como se manifestar no depoimento",
    LAWYER_ARRIVAL_REQUIRED: "Indique se o advogado chegou com antecedência",
    INSTRUCTIONS_CLARITY_REQUIRED: "Avalie a clareza das instruções (1 a 5)",
    INFO_CONFIDENCE_REQUIRED: "Avalie a segurança nas informações passadas (1 a 5)",
    HEARING_CONFIDENCE_REQUIRED: "Avalie a segurança do advogado na audiência (1 a 5)",
  },

  // ===== FORMULÁRIOS DE REGISTRO/EDIÇÃO =====
  REGISTRATION: {
    NAME_REQUIRED: "Informe seu nome completo",
    NAME_INVALID: "Nome deve ter pelo menos 3 caracteres",
    EMAIL_REQUIRED: "Informe seu e-mail",
    EMAIL_INVALID: "E-mail inválido",
    PHONE_REQUIRED: "Informe seu telefone com DDD",
    PHONE_INVALID: "Telefone inválido. Use o formato: (11) 99999-9999",
    CPF_REQUIRED: "Informe seu CPF",
    CPF_INVALID: "CPF inválido. Verifique o número",
    PIX_REQUIRED: "Informe uma chave PIX",
    PIX_INVALID: "Chave PIX inválida para o tipo selecionado",
  },

  // ===== MENSAGENS GENÉRICAS =====
  GENERIC: {
    REQUIRED_FIELD: "Este campo é obrigatório",
    INVALID_FORMAT: "Formato inválido",
    PLEASE_FILL_ALL_REQUIRED: "Preencha todos os campos obrigatórios marcados com *",
    NETWORK_ERROR: "Erro de conexão. Verifique sua internet e tente novamente",
    SERVER_ERROR: "Erro no servidor. Tente novamente mais tarde",
    CONTACT_SUPPORT: "Se o problema persistir, entre em contato com o suporte via WhatsApp",
  },
};

/**
 * Valida e-mail com mensagem específica
 */
export const validateEmail = (email: string): { isValid: boolean; message?: string } => {
  if (!email || email.trim() === "") {
    return { isValid: false, message: ValidationMessages.LOGIN.EMAIL_REQUIRED };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: ValidationMessages.LOGIN.EMAIL_INVALID };
  }

  return { isValid: true };
};

/**
 * Valida código de verificação
 */
export const validateVerificationCode = (code: string): { isValid: boolean; message?: string } => {
  if (!code || code.trim() === "") {
    return { isValid: false, message: ValidationMessages.LOGIN.CODE_REQUIRED };
  }

  if (code.length !== 6 || !/^\d{6}$/.test(code)) {
    return { isValid: false, message: ValidationMessages.LOGIN.CODE_REQUIRED };
  }

  return { isValid: true };
};

/**
 * Valida valor monetário
 */
export const validateCurrency = (value: string): { isValid: boolean; message?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: false, message: ValidationMessages.HEARING_REPORT.AGREEMENT_AMOUNT_REQUIRED };
  }

  const currencyRegex = /^R\$\s*[\d.,]+$/;
  if (!currencyRegex.test(value)) {
    return { isValid: false, message: ValidationMessages.HEARING_REPORT.AGREEMENT_AMOUNT_INVALID };
  }

  return { isValid: true };
};

/**
 * Valida descrição com comprimento mínimo
 */
export const validateDescription = (
  text: string,
  minLength: number = 50
): { isValid: boolean; message?: string } => {
  if (!text || text.trim() === "") {
    return { isValid: false, message: ValidationMessages.GENERIC.REQUIRED_FIELD };
  }

  if (text.length < minLength) {
    return {
      isValid: false,
      message: `Mínimo de ${minLength} caracteres (atual: ${text.length})`,
    };
  }

  return { isValid: true };
};

/**
 * Valida número inteiro positivo
 */
export const validatePositiveNumber = (
  value: string | number,
  fieldName: string = "campo"
): { isValid: boolean; message?: string } => {
  if (value === "" || value === null || value === undefined) {
    return { isValid: false, message: ValidationMessages.GENERIC.REQUIRED_FIELD };
  }

  const num = Number(value);
  if (isNaN(num) || num <= 0) {
    return { isValid: false, message: `${fieldName} deve ser um número maior que zero` };
  }

  return { isValid: true };
};

/**
 * Retorna mensagem de erro com contexto
 */
export const getErrorMessage = (fieldName: string, errorType: string): string => {
  const messages: Record<string, Record<string, string>> = {
    email: {
      required: ValidationMessages.LOGIN.EMAIL_REQUIRED,
      invalid: ValidationMessages.LOGIN.EMAIL_INVALID,
      notFound: ValidationMessages.LOGIN.EMAIL_NOT_FOUND,
    },
    code: {
      required: ValidationMessages.LOGIN.CODE_REQUIRED,
      invalid: ValidationMessages.LOGIN.CODE_INVALID,
    },
  };

  return messages[fieldName]?.[errorType] || ValidationMessages.GENERIC.REQUIRED_FIELD;
};
